import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Phone, Headphones, GraduationCap, Brain, Workflow, Layers, ShieldCheck, Sparkles, BookOpen, MessageSquare, BarChart3, Building2, Wrench, Cog, Tractor, Boxes, AlertTriangle, CheckCircle, Settings, PhoneCall, Mic, Network, Users, Target, Lightbulb, ChartBar, TrendingUp, Heart, ArrowRight, FileCheck, Bot } from "lucide-react";
import PresentationDownloadButton from "../components/PresentationDownloadButton";

const CALLREVU_LOGO = "https://www.callrevu.com/wp-content/uploads/2024/12/CR_Logo_Wordmark_Standard_RGB.png";
const CALLDRIP_LOGO_DARK = "https://www.calldrip.com/hubfs/calldrip-logo.svg";
const KUBOTA_ORANGE = "#fa6516";
const KUBOTA_ORANGE_LIGHT = "#fff1ea";
const KUBOTA_DARK = "#1c1c1c";

const slides = [
  {
    id: 1,
    type: "title",
    content: {
      eyebrow: "FOR KUBOTA CORPORATE",
      headline: "Expanding the Communication",
      highlight: "Ecosystem for Equipment Dealers",
      subhead: "CallRevu + Calldrip + AI Training + Communication Infrastructure",
      tagline: "A more complete customer communication platform for franchise and equipment dealers",
    },
  },
  {
    id: 2,
    type: "twoColumn",
    content: {
      label: "WHY THIS CONVERSATION MATTERS",
      headline: "Dealers Need More Than Just Lead Response",
      leftTitle: "Current dealer challenges",
      leftItems: [
        "Inconsistent phone handling",
        "No formal communication training",
        "Missed inbound opportunities",
        "Service communication breakdowns",
        "Limited visibility into customer interactions",
        "Multiple disconnected tools",
        "Staffing and onboarding challenges",
      ],
      rightTitle: "The opportunity",
      rightLead: "Build a more complete communication ecosystem that helps dealers:",
      rightItems: [
        "Improve customer experience",
        "Improve accountability",
        "Modernize operations",
        "Simplify technology",
        "Train employees faster",
      ],
    },
  },
  {
    id: 3,
    type: "realities",
    content: {
      label: "WHY THIS MATTERS IN EQUIPMENT",
      headline: "Equipment Dealers Are Different",
      sub: "Reality of the equipment space",
      points: [
        { icon: "users", text: "Many employees are operationally strong but have never had formal phone training" },
        { icon: "heart", text: "Old-school relationship culture still dominates" },
        { icon: "network", text: "Departments operate independently" },
        { icon: "layers", text: "Communication consistency varies heavily between stores" },
        { icon: "wrench", text: "Service and parts communication are critical retention drivers" },
      ],
      pull: "The relationship side of the business is still old-school… customer expectations are not.",
    },
  },
  {
    id: 4,
    type: "strengthsCompare",
    content: {
      label: "CURRENT STRENGTHS ALREADY IN PLACE",
      headline: "Existing Strengths Between the Organizations",
      callRevu: {
        title: "CallRevu Product Suite",
        products: [
          { name: "Hosted Phone Systems", desc: "Modern PBX, smart routing, business texting", icon: "phone" },
          { name: "Sales Vision", desc: "Inbound sales call tracking, scoring & accountability", icon: "target" },
          { name: "Service Vision", desc: "Service department call insights & retention drivers", icon: "wrench" },
          { name: "Test Track", desc: "AI phone roleplay & communication training", icon: "grad" },
        ],
      },
      calldrip: {
        title: "Calldrip Strengths",
        items: ["Speed-to-lead", "Outbound communication", "Texting", "Virtual assistants", "AI engagement", "Missed-call handling", "Dealership workflow understanding"],
      },
      combined: {
        title: "Combined Opportunity",
        lead: "A more complete communication platform from:",
        items: ["First customer interaction", "Through long-term retention"],
      },
    },
  },
  {
    id: 5,
    type: "spotlight",
    content: {
      label: "ONE OF THE MOST INTERESTING OPPORTUNITIES",
      headline: "AI Roleplay & Communication Training",
      sub: "Powered by CallRevu Test Track — AI Phone Roleplay & LMS",
      whyTitle: "Why Test Track stands out",
      why: [
        "Employees practice real customer conversations",
        "AI acts as the customer over live phone roleplay",
        "Repetition builds confidence",
        "Standardized training across every department",
        "Helps create consistency at scale",
      ],
      gapTitle: "Massive opportunity in equipment",
      gapLead: "Most dealerships have:",
      gap: ["No structured phone training", "No onboarding communication process", "No measurable certification process"],
    },
  },
  {
    id: 6,
    type: "departments",
    content: {
      label: "REAL USE CASES BY DEPARTMENT",
      headline: "Department-Specific Communication Training",
      cards: [
        { title: "Sales", icon: "target", items: ["Inbound tractor inquiry", "Trade conversations", "Financing questions", "Appointment setting"] },
        { title: "Service", icon: "wrench", items: ["Upset customer handling", "Delayed repair conversations", "Maintenance scheduling", "Status updates"] },
        { title: "Parts", icon: "boxes", items: ["Inventory conversations", "Backorder handling", "Upsell opportunities"] },
        { title: "Rental", icon: "tractor", items: ["Reservation management", "Availability conversations", "Equipment recommendation"] },
      ],
    },
  },
  {
    id: 7,
    type: "twoColumn",
    content: {
      label: "WHY THIS FITS KUBOTA & OEMS",
      headline: "OEM-Level Training Potential",
      leftTitle: "Potential long-term vision",
      leftItems: [
        "Kubota University integration",
        "Communication certification programs",
        "Standardized onboarding",
        "Department-specific learning paths",
        "Scalable dealer education",
      ],
      rightTitle: "Benefits to OEM",
      rightItems: [
        "Improved dealer consistency",
        "Stronger customer experience",
        "Easier onboarding",
        "Measurable improvement",
        "Modernized communication culture",
      ],
      footer: "This fills a major gap most OEMs currently do not address.",
    },
  },
  {
    id: 8,
    type: "twoColumn",
    content: {
      label: "CALLREVU HOSTED PHONE SYSTEMS",
      headline: "Communication Infrastructure Matters",
      leftTitle: "What CallRevu Hosted Phone delivers",
      leftItems: [
        "Hosted phone systems / cloud PBX",
        "Smarter routing & department call flows",
        "Voicemail management",
        "Mobile integrations",
        "Business texting",
        "Centralized communication",
      ],
      rightTitle: "Dealer value",
      rightItems: [
        "Fewer missed calls",
        "Easier management",
        "Improved customer experience",
        "Operational visibility",
        "Modernization without complexity",
      ],
    },
  },
  {
    id: 9,
    type: "consolidation",
    content: {
      label: "CONSOLIDATION OPPORTUNITY",
      headline: "Dealers Are Tired of Too Many Vendors",
      sub: "Current reality — dealers often use separate tools for:",
      tools: ["Phones", "Texting", "Analytics", "Lead response", "Training", "Reviews", "Communication tracking"],
      strategic: "Create a more unified communication ecosystem.",
      benefits: [
        "Simplified vendor stack",
        "Better reporting",
        "Stronger integrations",
        "Cleaner workflows",
        "Improved adoption",
      ],
    },
  },
  {
    id: 10,
    type: "formula",
    content: {
      label: "AI + ANALYTICS + TRAINING = HUGE POTENTIAL",
      headline: "The Real Opportunity Is Continuous Improvement",
      formula: [
        { label: "Training alone", desc: "is limited" },
        { label: "Analytics alone", desc: "is reactive" },
        { label: "Combined together", desc: "is transformative", highlight: true },
      ],
      results: [
        "Identify weaknesses",
        "Train specific scenarios",
        "Monitor improvement",
        "Reinforce behaviors",
        "Create accountability",
      ],
      pull: "Training becomes dramatically more powerful when tied directly to real customer interaction data.",
    },
  },
  {
    id: 11,
    type: "ecosystem",
    content: {
      label: "POTENTIAL STRATEGIC DIRECTION",
      headline: "Areas Worth Exploring Together",
      sub: "Communication ecosystem ideas",
      items: [
        { icon: "bot", text: "AI roleplay training (CallRevu Test Track)" },
        { icon: "fileCheck", text: "OEM certification programs" },
        { icon: "phone", text: "Hosted phone systems (CallRevu)" },
        { icon: "target", text: "Sales department analytics (Sales Vision)" },
        { icon: "wrench", text: "Service department analytics (Service Vision)" },
        { icon: "sparkles", text: "AI call summaries" },
        { icon: "phoneCall", text: "Missed-call automation (Calldrip)" },
        { icon: "mic", text: "After-hours AI engagement (Calldrip)" },
        { icon: "trending", text: "Inbound/outbound unified reporting" },
      ],
    },
  },
  {
    id: 12,
    type: "wantPair",
    content: {
      label: "WHAT DEALERS ACTUALLY WANT",
      headline: "Dealers Want Simple Solutions That Work",
      want: [
        "Fewer missed opportunities",
        "Easier onboarding",
        "Accountability",
        "Consistency",
        "Happier customers",
        "Less manager workload",
        "Practical tools",
      ],
      dontWant: [
        "Overly complex systems",
        "Corporate fluff",
        "Tools nobody uses",
        "More disconnected vendors",
      ],
    },
  },
  {
    id: 13,
    type: "closing",
    content: {
      label: "CLOSING VISION",
      headline: "Modern Communication",
      highlight: "Without Losing the Relationship Culture",
      final: [
        "Preserve the relationship-first culture dealers value",
        "Modernize communication expectations",
        "Use AI to support—not replace—people",
        "Create scalable systems that improve customer experience",
      ],
      closingLine: "The dealerships that win moving forward will combine old-school relationships with modern communication systems.",
    },
  },
];

// ============================== Icon helper ==============================
const IconRenderer = ({ name, size = 24, className = "", style = {} }) => {
  const icons = {
    phone: Phone, headphones: Headphones, grad: GraduationCap, brain: Brain,
    workflow: Workflow, layers: Layers, shield: ShieldCheck, sparkles: Sparkles,
    book: BookOpen, message: MessageSquare, chartBar: ChartBar, building: Building2,
    wrench: Wrench, cog: Cog, tractor: Tractor, boxes: Boxes, warn: AlertTriangle,
    check: CheckCircle, settings: Settings, phoneCall: PhoneCall, mic: Mic,
    network: Network, users: Users, target: Target, lightbulb: Lightbulb,
    trending: TrendingUp, heart: Heart, fileCheck: FileCheck, bot: Bot,
  };
  const Icon = icons[name] || Sparkles;
  return <Icon size={size} className={className} style={style} />;
};

// ============================== Shared blocks ==============================
const SlideShell = ({ children, padX = "px-20", padY = "py-14" }) => (
  <div className={`flex-1 flex flex-col justify-center ${padX} ${padY}`} style={{ color: KUBOTA_DARK }}>
    {children}
  </div>
);

const Eyebrow = ({ children }) => (
  <div
    className="text-base font-bold tracking-[0.35em] mb-4"
    style={{ color: KUBOTA_ORANGE }}
  >
    {children}
  </div>
);

const H2 = ({ children, sub }) => (
  <>
    <h2 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-3" style={{ color: KUBOTA_DARK }}>
      {children}
    </h2>
    {sub && <p className="text-xl text-gray-500 mb-6">{sub}</p>}
  </>
);

const Bullet = ({ children, accent = KUBOTA_ORANGE }) => (
  <li className="flex items-start gap-4 py-2.5">
    <span
      className="flex-shrink-0 mt-2.5 w-2 h-2 rounded-full"
      style={{ backgroundColor: accent }}
    />
    <span className="text-xl text-gray-800 leading-relaxed">{children}</span>
  </li>
);

const Card = ({ children, className = "", borderColor = "#e5e7eb" }) => (
  <div
    className={`rounded-2xl bg-white border p-8 shadow-sm ${className}`}
    style={{ borderColor }}
  >
    {children}
  </div>
);

// ============================== Slide renderers ==============================
const TitleSlide = ({ content }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-16 py-14">
    <div className="flex items-center gap-12 mb-16">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-14 opacity-90" />
      <div className="w-px h-12 bg-gray-300" />
      <img src={CALLDRIP_LOGO_DARK} alt="Calldrip" className="h-12 opacity-90" />
    </div>
    <div className="text-base font-bold tracking-[0.4em] mb-8" style={{ color: KUBOTA_ORANGE }}>
      {content.eyebrow}
    </div>
    <h1 className="text-6xl md:text-7xl font-light leading-[1.05] max-w-6xl" style={{ color: KUBOTA_DARK }}>
      {content.headline}
    </h1>
    <h1 className="text-6xl md:text-7xl font-bold leading-[1.05] max-w-6xl mt-3 mb-10" style={{ color: KUBOTA_ORANGE }}>
      {content.highlight}
    </h1>
    <p className="text-2xl text-gray-700 max-w-4xl mb-4">{content.subhead}</p>
    <p className="text-lg text-gray-500 max-w-3xl italic">{content.tagline}</p>
  </div>
);

const TwoColumnSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2>{content.headline}</H2>
    <div className="grid grid-cols-2 gap-10 mt-8">
      <Card>
        <h3 className="text-base font-bold uppercase tracking-widest mb-5" style={{ color: KUBOTA_ORANGE }}>
          {content.leftTitle}
        </h3>
        <ul className="space-y-0">
          {content.leftItems.map((it, i) => <Bullet key={i}>{it}</Bullet>)}
        </ul>
      </Card>
      <Card borderColor={KUBOTA_ORANGE}>
        <h3 className="text-base font-bold uppercase tracking-widest mb-5" style={{ color: KUBOTA_ORANGE }}>
          {content.rightTitle}
        </h3>
        {content.rightLead && (
          <p className="text-gray-700 mb-3 text-lg">{content.rightLead}</p>
        )}
        <ul className="space-y-0">
          {content.rightItems.map((it, i) => <Bullet key={i} accent={KUBOTA_ORANGE}>{it}</Bullet>)}
        </ul>
      </Card>
    </div>
    {content.footer && (
      <div className="mt-8 rounded-xl px-8 py-5 text-center text-xl italic" style={{ backgroundColor: KUBOTA_ORANGE_LIGHT, color: KUBOTA_DARK }}>
        “{content.footer}”
      </div>
    )}
  </SlideShell>
);

const RealitiesSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2 sub={content.sub}>{content.headline}</H2>
    <div className="grid grid-cols-2 gap-x-10 gap-y-5 mt-4 content-start">
      {content.points.map((p, i) => (
        <div key={i} className="flex items-start gap-5 rounded-xl border bg-white p-6 shadow-sm" style={{ borderColor: "#e5e7eb" }}>
          <div
            className="flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: KUBOTA_ORANGE_LIGHT }}
          >
            <IconRenderer name={p.icon} size={26} style={{ color: KUBOTA_ORANGE }} />
          </div>
          <p className="text-xl text-gray-800 leading-relaxed pt-2">{p.text}</p>
        </div>
      ))}
    </div>
    <div
      className="mt-10 rounded-2xl px-10 py-7 text-2xl italic font-medium text-center max-w-5xl mx-auto"
      style={{ backgroundColor: KUBOTA_DARK, color: "#fff", borderLeft: `6px solid ${KUBOTA_ORANGE}` }}
    >
      “{content.pull}”
    </div>
  </SlideShell>
);

const StrengthsCompareSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2>{content.headline}</H2>
    <div className="grid grid-cols-2 gap-6 mt-6">
      <Card borderColor={KUBOTA_ORANGE}>
        <div className="flex items-center gap-3 mb-4">
          <img src={CALLREVU_LOGO} alt="CallRevu" className="h-7" />
        </div>
        <h3 className="text-base font-bold uppercase tracking-widest mb-5 text-gray-500">{content.callRevu.title}</h3>
        <div className="grid grid-cols-2 gap-4">
          {content.callRevu.products.map((p, i) => (
            <div key={i} className="rounded-lg border bg-gray-50 p-4" style={{ borderColor: "#e5e7eb" }}>
              <div className="flex items-center gap-2.5 mb-2">
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: KUBOTA_ORANGE_LIGHT }}
                >
                  <IconRenderer name={p.icon} size={18} style={{ color: KUBOTA_ORANGE }} />
                </div>
                <span className="text-lg font-bold" style={{ color: KUBOTA_DARK }}>{p.name}</span>
              </div>
              <p className="text-sm text-gray-600 leading-snug">{p.desc}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <img src={CALLDRIP_LOGO_DARK} alt="Calldrip" className="h-7" />
        </div>
        <h3 className="text-base font-bold uppercase tracking-widest mb-3 text-gray-500">{content.calldrip.title}</h3>
        <ul>{content.calldrip.items.map((it, i) => <Bullet key={i}>{it}</Bullet>)}</ul>
      </Card>
    </div>
    <div className="mt-8 rounded-2xl p-7" style={{ backgroundColor: KUBOTA_ORANGE_LIGHT, borderLeft: `6px solid ${KUBOTA_ORANGE}` }}>
      <h3 className="text-base font-bold uppercase tracking-widest mb-2" style={{ color: KUBOTA_ORANGE }}>
        {content.combined.title}
      </h3>
      <p className="text-lg text-gray-700 mb-3">{content.combined.lead}</p>
      <div className="flex items-center gap-6 text-xl font-semibold" style={{ color: KUBOTA_DARK }}>
        {content.combined.items.map((it, i, arr) => (
          <span key={i} className="flex items-center gap-3">
            <span>{it}</span>
            {i < arr.length - 1 && <ArrowRight size={22} style={{ color: KUBOTA_ORANGE }} />}
          </span>
        ))}
      </div>
    </div>
  </SlideShell>
);

const SpotlightSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2 sub={content.sub}>{content.headline}</H2>
    <div className="grid grid-cols-2 gap-8 mt-6">
      <Card borderColor={KUBOTA_ORANGE}>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: KUBOTA_ORANGE_LIGHT }}>
            <Sparkles size={24} style={{ color: KUBOTA_ORANGE }} />
          </div>
          <h3 className="text-base font-bold uppercase tracking-widest" style={{ color: KUBOTA_ORANGE }}>{content.whyTitle}</h3>
        </div>
        <ul>{content.why.map((it, i) => <Bullet key={i} accent={KUBOTA_ORANGE}>{it}</Bullet>)}</ul>
      </Card>
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gray-100">
            <AlertTriangle size={24} className="text-gray-500" />
          </div>
          <h3 className="text-base font-bold uppercase tracking-widest text-gray-500">{content.gapTitle}</h3>
        </div>
        <p className="text-lg text-gray-700 mb-3">{content.gapLead}</p>
        <ul>{content.gap.map((it, i) => <Bullet key={i} accent="#9ca3af">{it}</Bullet>)}</ul>
      </Card>
    </div>
  </SlideShell>
);

const DepartmentsSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2>{content.headline}</H2>
    <div className="grid grid-cols-4 gap-6 mt-10">
      {content.cards.map((c, i) => (
        <Card key={i} className="flex flex-col" borderColor="#e5e7eb">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
            style={{ backgroundColor: KUBOTA_ORANGE_LIGHT }}
          >
            <IconRenderer name={c.icon} size={28} style={{ color: KUBOTA_ORANGE }} />
          </div>
          <h3 className="text-2xl font-bold mb-4" style={{ color: KUBOTA_DARK }}>{c.title}</h3>
          <ul className="space-y-1.5">
            {c.items.map((it, j) => (
              <li key={j} className="text-base text-gray-700 leading-relaxed">— {it}</li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  </SlideShell>
);

const ConsolidationSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2 sub={content.sub}>{content.headline}</H2>
    <div className="flex flex-col mt-6 gap-8">
      <div className="flex flex-wrap gap-3 justify-center">
        {content.tools.map((t, i) => (
          <div
            key={i}
            className="px-6 py-3 rounded-full text-base font-medium border bg-gray-50 text-gray-700"
            style={{ borderColor: "#e5e7eb" }}
          >
            {t}
          </div>
        ))}
      </div>
      <div className="text-center text-3xl italic font-semibold mt-4" style={{ color: KUBOTA_DARK }}>
        →&nbsp;&nbsp;<span style={{ color: KUBOTA_ORANGE }}>{content.strategic}</span>
      </div>
      <div className="grid grid-cols-5 gap-4 mt-4">
        {content.benefits.map((b, i) => (
          <div
            key={i}
            className="rounded-xl p-5 text-center border"
            style={{ backgroundColor: KUBOTA_ORANGE_LIGHT, borderColor: KUBOTA_ORANGE + "40" }}
          >
            <CheckCircle size={22} className="mx-auto mb-2" style={{ color: KUBOTA_ORANGE }} />
            <p className="text-base font-semibold" style={{ color: KUBOTA_DARK }}>{b}</p>
          </div>
        ))}
      </div>
    </div>
  </SlideShell>
);

const FormulaSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2>{content.headline}</H2>
    <div className="grid grid-cols-3 gap-5 mt-8 mb-8">
      {content.formula.map((f, i) => (
        <div
          key={i}
          className={`rounded-2xl p-8 text-center border ${f.highlight ? "shadow-md" : ""}`}
          style={{
            backgroundColor: f.highlight ? KUBOTA_ORANGE : "#f9fafb",
            borderColor: f.highlight ? KUBOTA_ORANGE : "#e5e7eb",
            color: f.highlight ? "#fff" : KUBOTA_DARK,
          }}
        >
          <div className="text-sm font-bold tracking-widest opacity-70 mb-3">STEP {i + 1}</div>
          <div className="text-2xl font-bold mb-3">{f.label}</div>
          <div className={f.highlight ? "text-base opacity-90" : "text-base text-gray-500"}>{f.desc}</div>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-5 gap-4">
      {content.results.map((r, i) => (
        <Card key={i} className="text-center" >
          <CheckCircle size={22} className="mx-auto mb-2" style={{ color: KUBOTA_ORANGE }} />
          <p className="text-base font-semibold" style={{ color: KUBOTA_DARK }}>{r}</p>
        </Card>
      ))}
    </div>
    <div
      className="mt-8 rounded-2xl px-10 py-6 text-xl italic font-medium text-center"
      style={{ backgroundColor: KUBOTA_DARK, color: "#fff", borderLeft: `6px solid ${KUBOTA_ORANGE}` }}
    >
      “{content.pull}”
    </div>
  </SlideShell>
);

const EcosystemSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2 sub={content.sub}>{content.headline}</H2>
    <div className="grid grid-cols-3 gap-6 mt-8 content-start">
      {content.items.map((it, i) => (
        <div
          key={i}
          className="flex items-center gap-5 rounded-xl border bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
          style={{ borderColor: "#e5e7eb" }}
        >
          <div
            className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: KUBOTA_ORANGE_LIGHT }}
          >
            <IconRenderer name={it.icon} size={26} style={{ color: KUBOTA_ORANGE }} />
          </div>
          <p className="text-lg font-semibold leading-snug" style={{ color: KUBOTA_DARK }}>{it.text}</p>
        </div>
      ))}
    </div>
  </SlideShell>
);

const WantPairSlide = ({ content }) => (
  <SlideShell>
    <Eyebrow>{content.label}</Eyebrow>
    <H2>{content.headline}</H2>
    <div className="grid grid-cols-2 gap-8 mt-6">
      <Card borderColor={KUBOTA_ORANGE}>
        <div className="flex items-center gap-3 mb-5">
          <CheckCircle size={26} style={{ color: KUBOTA_ORANGE }} />
          <h3 className="text-base font-bold uppercase tracking-widest" style={{ color: KUBOTA_ORANGE }}>They want</h3>
        </div>
        <ul>{content.want.map((it, i) => <Bullet key={i} accent={KUBOTA_ORANGE}>{it}</Bullet>)}</ul>
      </Card>
      <Card>
        <div className="flex items-center gap-3 mb-5">
          <AlertTriangle size={26} className="text-gray-500" />
          <h3 className="text-base font-bold uppercase tracking-widest text-gray-500">They DO NOT want</h3>
        </div>
        <ul>{content.dontWant.map((it, i) => <Bullet key={i} accent="#9ca3af">{it}</Bullet>)}</ul>
      </Card>
    </div>
  </SlideShell>
);

const ClosingSlide = ({ content }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center px-16 py-14">
    <div className="flex items-center gap-12 mb-14">
      <img src={CALLREVU_LOGO} alt="CallRevu" className="h-12 opacity-90" />
      <div className="w-px h-12 bg-gray-300" />
      <img src={CALLDRIP_LOGO_DARK} alt="Calldrip" className="h-11 opacity-90" />
    </div>
    <Eyebrow>{content.label}</Eyebrow>
    <h1 className="text-6xl md:text-7xl font-light max-w-6xl leading-[1.05]" style={{ color: KUBOTA_DARK }}>
      {content.headline}
    </h1>
    <h1 className="text-6xl md:text-7xl font-bold max-w-6xl mt-3 mb-10" style={{ color: KUBOTA_ORANGE }}>
      {content.highlight}
    </h1>
    <ul className="text-left max-w-3xl mb-12">
      {content.final.map((f, i) => <Bullet key={i} accent={KUBOTA_ORANGE}>{f}</Bullet>)}
    </ul>
    <div
      className="rounded-2xl px-10 py-7 max-w-4xl text-2xl italic font-medium"
      style={{ backgroundColor: KUBOTA_DARK, color: "#fff", borderLeft: `6px solid ${KUBOTA_ORANGE}` }}
    >
      “{content.closingLine}”
    </div>
  </div>
);

// ============================== Slide router ==============================
const SlideRenderer = ({ slide }) => {
  const renderers = {
    title: TitleSlide,
    twoColumn: TwoColumnSlide,
    realities: RealitiesSlide,
    strengthsCompare: StrengthsCompareSlide,
    spotlight: SpotlightSlide,
    departments: DepartmentsSlide,
    consolidation: ConsolidationSlide,
    formula: FormulaSlide,
    ecosystem: EcosystemSlide,
    wantPair: WantPairSlide,
    closing: ClosingSlide,
  };
  const C = renderers[slide.type];
  return C ? <C content={slide.content} /> : null;
};

// ============================== Page ==============================
export default function PresentationsKubota() {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const next = useCallback(() => setCurrent((c) => Math.min(c + 1, slides.length - 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "f" || e.key === "F") toggleFullscreen();
      else if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev, toggleFullscreen, isFullscreen]);

  const slide = slides[current];

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, #ffffff 0%, #f7f7f8 100%)", color: KUBOTA_DARK }}
      data-testid="kubota-presentation"
    >
      {/* Slide area */}
      <div className="flex-1 flex flex-col relative" style={{ minHeight: "calc(100vh - 72px)" }}>
        <div className="w-full max-w-[1600px] mx-auto flex flex-col flex-1">
          <SlideRenderer slide={slide} />
        </div>

        {/* Nav arrows */}
        <button
          onClick={prev}
          disabled={current === 0}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="prev-slide-btn"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={next}
          disabled={current === slides.length - 1}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          data-testid="next-slide-btn"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-600 text-sm" data-testid="slide-counter">
            Slide {current + 1} of {slides.length}
          </span>
          <div className="flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === current ? "32px" : "6px",
                  backgroundColor: i === current ? KUBOTA_ORANGE : "#d1d5db",
                }}
                data-testid={`slide-dot-${i + 1}`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <PresentationDownloadButton
            slides={slides}
            renderSlide={(s) => (
              <div style={{ width: "1920px", height: "1080px", backgroundColor: "#ffffff", position: "relative", color: KUBOTA_DARK, display: "flex", flexDirection: "column" }}>
                <SlideRenderer slide={s} />
              </div>
            )}
            filename="Kubota-Communication-Ecosystem"
            bgColor="#ffffff"
            className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:cursor-not-allowed shadow-sm"
          />
          <span className="text-gray-400 text-xs hidden lg:inline">
            Press <kbd className="bg-gray-100 px-1.5 py-0.5 rounded">F</kbd> for fullscreen | <kbd className="bg-gray-100 px-1 py-0.5 rounded">←</kbd> <kbd className="bg-gray-100 px-1 py-0.5 rounded">→</kbd> to navigate
          </span>
          <button
            onClick={toggleFullscreen}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors"
            style={{ backgroundColor: KUBOTA_ORANGE }}
            data-testid="present-btn"
          >
            {isFullscreen ? "Exit Fullscreen" : "Present"}
          </button>
        </div>
      </div>
    </div>
  );
}
